/**
 * ─── Jewelry PriceCalculator (Domain Service) ────────────────
 * Pure function: (item, rateCard, promo) → price breakdown.
 * No side effects, fully testable in isolation.
 *
 * Price Formula:
 *   Metal Value = weight × rate × purityFactor
 *   Making Charge = fixed | perGram × weight | percent × MetalValue
 *   Wastage = MetalValue × wastagePercent / 100
 *   Subtotal = MetalValue + MakingCharge + Wastage
 *   Old Gold Credit = oldGoldWeight × rate × oldGoldPurityFactor
 *   Grand Total = Subtotal − OldGoldCredit − Discount
 */

import type {
  JewelryItem, RateCard, SalesOrderLine,
  MetalType, OrderLineType,
} from './jewelry.schema'

export interface PriceBreakdown {
  metalValue: number
  makingCharge: number
  wastage: number
  subtotal: number
  discountAmount: number
  oldGoldCredit: number
  grandTotal: number
  rateUsed: number
  rateEffectiveAt: number
}

export interface OldGoldInput {
  weightGrams: number
  purityKarat: number
  appraisalValue?: number          // Override if manually appraised
}

export interface PriceCalculatorInput {
  item: Pick<JewelryItem, 'weightGrams' | 'purityKarat' | 'makingChargeType' | 'makingChargeValue' | 'wastagePercent'>
  rateCard: Pick<RateCard, 'ratePerGram' | 'metalType' | 'purityKarat' | 'effectiveAt'>
  oldGold?: OldGoldInput
  discountPercent?: number
  discountAmount?: number
}

/**
 * Domain Service: PriceCalculator
 * Pure function — no state, no side effects.
 */
export class PriceCalculator {
  /**
   * Calculate the complete price breakdown for a jewelry item.
   */
  static calculate(input: PriceCalculatorInput): PriceBreakdown {
    const { item, rateCard, oldGold, discountPercent, discountAmount } = input

    // Metal fineness factor (24K = 1.0, 18K = 0.75, etc.)
    const purityFactor = item.purityKarat / 24

    // ─── Metal Value ──────────────────────────────────────
    const metalValue = Math.round(item.weightGrams * rateCard.ratePerGram * purityFactor * 100) / 100

    // ─── Making Charge ────────────────────────────────────
    let makingCharge = 0
    switch (item.makingChargeType) {
      case 'fixed':
        makingCharge = item.makingChargeValue
        break
      case 'per_gram':
        makingCharge = item.makingChargeValue * item.weightGrams
        break
      case 'percent':
        makingCharge = metalValue * (item.makingChargeValue / 100)
        break
    }
    makingCharge = Math.round(makingCharge * 100) / 100

    // ─── Wastage ──────────────────────────────────────────
    const wastage = Math.round(metalValue * (item.wastagePercent / 100) * 100) / 100

    // ─── Subtotal ─────────────────────────────────────────
    const subtotal = Math.round((metalValue + makingCharge + wastage) * 100) / 100

    // ─── Discount ─────────────────────────────────────────
    let appliedDiscount = 0
    if (discountAmount && discountAmount > 0) {
      appliedDiscount = discountAmount
    } else if (discountPercent && discountPercent > 0) {
      appliedDiscount = Math.round(subtotal * (discountPercent / 100) * 100) / 100
    }

    // ─── Old-Gold Exchange ("Palit-Luma") ─────────────────
    let oldGoldCredit = 0
    if (oldGold) {
      if (oldGold.appraisalValue && oldGold.appraisalValue > 0) {
        oldGoldCredit = oldGold.appraisalValue
      } else {
        const oldGoldPurityFactor = oldGold.purityKarat / 24
        oldGoldCredit = Math.round(
          oldGold.weightGrams * rateCard.ratePerGram * oldGoldPurityFactor * 100
        ) / 100
      }
    }

    // ─── Grand Total ──────────────────────────────────────
    const grandTotal = Math.max(0, Math.round(
      (subtotal - appliedDiscount - oldGoldCredit) * 100
    ) / 100)

    return {
      metalValue,
      makingCharge,
      wastage,
      subtotal,
      discountAmount: appliedDiscount,
      oldGoldCredit,
      grandTotal,
      rateUsed: rateCard.ratePerGram,
      rateEffectiveAt: rateCard.effectiveAt,
    }
  }

  /**
   * Build a SalesOrderLine with snapped price values.
   * This is the "snapshot" — once created, these values are NEVER
   * recomputed from a later rate_card row (per PRD Part 1.5).
   */
  static buildOrderLine(
    item: JewelryItem,
    rateCard: RateCard,
    lineType: OrderLineType = 'sale',
    discountAmount: number = 0,
  ): Omit<SalesOrderLine, 'id' | 'salesOrderId' | 'createdAt'> {
    const breakdown = PriceCalculator.calculate({
      item,
      rateCard,
      discountAmount,
    })

    return {
      jewelryItemId: item.id,
      tagNumber: item.tagNumber,
      description: item.description,
      weightGrams: item.weightGrams,
      ratePerGramSnapshot: rateCard.ratePerGram,
      metalValueSnapshot: breakdown.metalValue,
      makingChargeSnapshot: breakdown.makingCharge,
      wastageSnapshot: breakdown.wastage,
      discountAmount: breakdown.discountAmount,
      lineTotal: breakdown.subtotal - breakdown.discountAmount,
      lineType,
    }
  }

  /**
   * Build old-gold exchange order line.
   */
  static buildOldGoldLine(
    oldGold: OldGoldInput,
    rateCard: RateCard,
  ): Omit<SalesOrderLine, 'id' | 'salesOrderId' | 'createdAt'> {
    const purityFactor = oldGold.purityKarat / 24
    const appraisalValue = oldGold.appraisalValue
      ?? Math.round(oldGold.weightGrams * rateCard.ratePerGram * purityFactor * 100) / 100

    return {
      description: `Old Gold Exchange — ${oldGold.weightGrams}g, ${oldGold.purityKarat}K`,
      weightGrams: oldGold.weightGrams,
      ratePerGramSnapshot: rateCard.ratePerGram,
      metalValueSnapshot: 0,
      makingChargeSnapshot: 0,
      wastageSnapshot: 0,
      discountAmount: 0,
      lineTotal: -appraisalValue,  // Negative = credit to customer
      lineType: 'old_gold_exchange',
      oldGoldWeight: oldGold.weightGrams,
      oldGoldPurity: oldGold.purityKarat,
      oldGoldAppraisalValue: appraisalValue,
    }
  }

  /**
   * Compute customer loyalty tier based on total purchases.
   */
  static computeLoyaltyTier(totalPurchases: number): 'regular' | 'silver' | 'gold' | 'platinum' {
    if (totalPurchases >= 500000) return 'platinum'
    if (totalPurchases >= 200000) return 'gold'
    if (totalPurchases >= 50000) return 'silver'
    return 'regular'
  }

  /**
   * Check if a cash transaction exceeds AMLA covered transaction threshold.
   * Default: PHP 1,000,000 for jewelry dealers (configurable).
   */
  static isAMLACoveredTransaction(cashAmount: number, threshold: number = 1000000): boolean {
    return cashAmount >= threshold
  }
}
